import { useState, useEffect } from 'react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';

// ── Category config ────────────────────────────────────────────────────────────

interface CategoryInfo {
  id: 'functional' | 'analytics' | 'preferences';
  label: string;
  description: string;
}

const CATEGORIES: CategoryInfo[] = [
  {
    id: 'functional',
    label: 'Functional',
    description:
      'Remembers your navigation preferences, selected clinic, tenant context, and sidebar state. Without these, settings reset each visit.',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description:
      'First-party usage analytics to help us improve the platform. No third-party trackers.',
  },
  {
    id: 'preferences',
    label: 'Preferences',
    description:
      'Stores theme and locale choices so the app looks and reads the way you expect.',
  },
];

// ── Component ──────────────────────────────────────────────────────────────────

interface CookieConsentPreferencesProps {
  open: boolean;
}

export function CookieConsentPreferences({ open }: CookieConsentPreferencesProps) {
  const { consent, customize, acceptAll, closePreferences } = useCookieConsent();

  // Local toggle state — initialized from current consent (or all-off for new visitors)
  const [toggles, setToggles] = useState({
    functional: consent?.functional ?? false,
    analytics: consent?.analytics ?? false,
    preferences: consent?.preferences ?? false,
  });

  // Sync local state when consent changes externally (e.g. accept all from banner)
  useEffect(() => {
    if (consent) {
      setToggles({
        functional: consent.functional,
        analytics: consent.analytics,
        preferences: consent.preferences,
      });
    }
  }, [consent]);

  function handleSave() {
    customize(toggles);
    closePreferences();
  }

  function handleAcceptAll() {
    acceptAll();
    closePreferences();
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && closePreferences()}>
      <SheetContent
        side="right"
        className="flex flex-col sm:max-w-md"
        aria-label="Cookie preferences"
      >
        <SheetHeader>
          <SheetTitle>Cookie Preferences</SheetTitle>
          <SheetDescription>
            Choose which optional cookies you allow. Essential cookies are always
            active to keep the app working.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto py-4">
          {/* Essential — always on */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex items-center gap-3">
              <Checkbox checked disabled aria-label="Essential cookies (always active)" />
              <div>
                <p className="text-sm font-medium">Essential</p>
                <p className="text-xs text-muted-foreground">
                  Authentication, CSRF protection, and this consent cookie.
                  Required for the app to function. Always active.
                </p>
              </div>
            </div>
          </div>

          {/* Optional categories */}
          {CATEGORIES.map((cat) => (
            <div key={cat.id} className="rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id={`consent-${cat.id}`}
                  checked={toggles[cat.id]}
                  onCheckedChange={(checked) =>
                    setToggles((prev) => ({ ...prev, [cat.id]: checked === true }))
                  }
                  aria-label={`${cat.label} cookies`}
                />
                <label htmlFor={`consent-${cat.id}`} className="cursor-pointer">
                  <p className="text-sm font-medium">{cat.label}</p>
                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                </label>
              </div>
            </div>
          ))}
        </div>

        <SheetFooter className="gap-2 border-t pt-4 sm:flex-row">
          <Button variant="outline" size="sm" onClick={handleSave} className="flex-1">
            Save preferences
          </Button>
          <Button variant="outline" size="sm" onClick={handleAcceptAll} className="flex-1">
            Accept all
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
